import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import api from '../../services/api';
import { X, Save, Clock, DollarSign, Plus } from 'lucide-react';

interface TimesheetFormProps {
    onClose: () => void;
    onSuccess: () => void;
    entry?: any;
    projects?: any[];
    customers?: any[];
}

const TimesheetForm: React.FC<TimesheetFormProps> = ({ onClose, onSuccess, entry, projects: initialProjects = [], customers: initialCustomers = [] }) => {
    const [projects, setProjects] = useState(initialProjects);
    const [customers, setCustomers] = useState(initialCustomers);
    const [tasks, setTasks] = useState<any[]>([]);
    const [selectedProject, setSelectedProject] = useState('');

    // Inline Creation State
    const [showProjectCreator, setShowProjectCreator] = useState(false);
    const [showTaskCreator, setShowTaskCreator] = useState(false);
    const [showCustomerCreator, setShowCustomerCreator] = useState(false);

    // New Project State
    const [newProject, setNewProject] = useState({ projectName: '', customerId: '', billingMethod: 'Hourly Credit', hourlyRate: 0, receivedAmount: 0 });
    // New Task State
    const [newTask, setNewTask] = useState({ taskName: '', isBillable: true });
    // New Customer State
    const [newCustomer, setNewCustomer] = useState({ displayName: '', customerType: 'Business', email: '', companyName: '' });

    const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm({
        defaultValues: {
            date: new Date().toISOString().split('T')[0],
            projectId: '',
            taskId: '',
            timeSpent: '',
            isBillable: true,
            billingStatus: 'Unbilled',
            notes: ''
        }
    });

    const projectId = watch('projectId');
    const timeSpent = watch('timeSpent');

    const totalAmount = React.useMemo(() => {
        if (!projectId) return '0.00';

        const project = projects.find((p: any) => p.id === projectId);
        if (!project) return '0.00';

        if (project.billingMethod === 'Fixed Cost') {
            return Number(project.hourlyRate || 0).toFixed(2);
        }

        if (project.billingMethod === 'Non-Billable') {
            return '0.00';
        }

        if (!timeSpent || !project.hourlyRate) return '0.00';

        const [hours, minutes] = timeSpent.split(':').map(Number);
        if (isNaN(hours) || isNaN(minutes)) return '0.00';

        const totalHours = hours + (minutes / 60);
        return (totalHours * project.hourlyRate).toFixed(2);
    }, [projectId, timeSpent, projects]);

    useEffect(() => {
        setProjects(initialProjects);
        setCustomers(initialCustomers);
    }, [initialProjects, initialCustomers]);

    useEffect(() => {
        if (entry) {
            reset({
                ...entry,
                date: new Date(entry.date).toISOString().split('T')[0],
            });
            setSelectedProject(entry.projectId);
        }
    }, [entry, reset]);

    useEffect(() => {
        if (projectId) {
            setSelectedProject(projectId);
            // Fetch tasks for project
            const project = projects.find((p: any) => p.id === projectId);
            if (project && project.tasks) {
                setTasks(project.tasks);
            } else {
                setTasks([]);
            }
        } else {
            setTasks([]);
        }
    }, [projectId, projects]);

    const createProject = async () => {
        try {
            if (!newProject.projectName || !newProject.customerId) return alert('Project Name and Customer are required');
            const res = await api.post('/projects', newProject);
            const created = res.data;
            // Add to local projects list
            setProjects(prev => [created, ...prev]);
            // Select it
            setValue('projectId', created.id);
            setShowProjectCreator(false);
            setNewProject({ projectName: '', customerId: '', billingMethod: 'Hourly Credit', hourlyRate: 0, receivedAmount: 0 });
        } catch (err) {
            console.error(err);
            alert('Failed to create project');
        }
    };

    const createTask = async () => {
        try {
            if (!newTask.taskName || !projectId) return alert('Task Name and Project are required');
            const res = await api.post(`/projects/${projectId}/tasks`, newTask);
            const created = res.data;

            // Update local projects structure to include new task
            setProjects(prev => prev.map((p: any) => {
                if (p.id === projectId) {
                    return { ...p, tasks: [...(p.tasks || []), created] };
                }
                return p;
            }));

            // Select it
            setValue('taskId', created.id);
            setShowTaskCreator(false);
            setNewTask({ taskName: '', isBillable: true });
        } catch (err) {
            console.error(err);
            alert('Failed to create task');
        }
    };

    const createCustomer = async () => {
        try {
            if (!newCustomer.displayName) return alert('Display Name is required');
            const payload = {
                ...newCustomer,
                name: newCustomer.displayName // Backend requires 'name'
            };
            const res = await api.post('/customers', payload);
            const created = res.data;

            // Add to local customers list
            setCustomers(prev => [created, ...prev]);

            // Auto select in project creator if it's open
            setNewProject(prev => ({ ...prev, customerId: created.id }));

            setShowCustomerCreator(false);
            setNewCustomer({ displayName: '', customerType: 'Business', email: '', companyName: '' });
        } catch (err) {
            console.error(err);
            alert('Failed to create customer');
        }
    };

    const onSubmit = async (data: any) => {
        try {
            const formattedData = {
                date: data.date,
                timeSpent: data.timeSpent,
                projectId: data.projectId,
                taskId: data.taskId || undefined,
                customerId: data.customerId || undefined,
                isBillable: true,
                billingStatus: data.billingStatus,
                notes: data.notes
            };

            if (entry && entry.id) {
                await api.put(`/timesheets/${entry.id}`, formattedData);
            } else {
                await api.post('/timesheets', formattedData);
            }
            onSuccess();
        } catch (err) {
            console.error('Failed to save timesheet', err);
            alert('Failed to save timesheet');
        }
    };

    if (showCustomerCreator) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                    <h3 className="text-lg font-bold mb-4">Create New Customer</h3>
                    <div className="space-y-4">
                        <input
                            placeholder="Display Name*"
                            className="w-full border p-2 rounded"
                            value={newCustomer.displayName}
                            onChange={e => setNewCustomer({ ...newCustomer, displayName: e.target.value })}
                        />
                        <select
                            className="w-full border p-2 rounded"
                            value={newCustomer.customerType}
                            onChange={e => setNewCustomer({ ...newCustomer, customerType: e.target.value })}
                        >
                            <option value="Business">Business</option>
                            <option value="Individual">Individual</option>
                        </select>
                        <input
                            placeholder="Company Name"
                            className="w-full border p-2 rounded"
                            value={newCustomer.companyName}
                            onChange={e => setNewCustomer({ ...newCustomer, companyName: e.target.value })}
                        />
                        <input
                            placeholder="Email"
                            className="w-full border p-2 rounded"
                            value={newCustomer.email}
                            onChange={e => setNewCustomer({ ...newCustomer, email: e.target.value })}
                        />
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setShowCustomerCreator(false)} className="px-4 py-2 border rounded">Cancel</button>
                            <button onClick={createCustomer} className="px-4 py-2 bg-blue-600 text-white rounded">Create</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (showProjectCreator) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                    <h3 className="text-lg font-bold mb-4">Create New Project</h3>
                    <div className="space-y-4">
                        <input
                            placeholder="Project Name*"
                            className="w-full border p-2 rounded"
                            value={newProject.projectName}
                            onChange={e => setNewProject({ ...newProject, projectName: e.target.value })}
                        />
                        <div className="flex gap-2">
                            <select
                                className="w-full border p-2 rounded"
                                value={newProject.customerId}
                                onChange={e => setNewProject({ ...newProject, customerId: e.target.value })}
                            >
                                <option value="">Select Customer*</option>
                                {customers.map((c: any) => (
                                    <option key={c.id} value={c.id}>{c.displayName}</option>
                                ))}
                            </select>
                            <button type="button" onClick={() => setShowCustomerCreator(true)} className="p-2 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 text-gray-600" title="Create New Customer">
                                <Plus size={20} />
                            </button>
                        </div>
                        <select
                            className="w-full border p-2 rounded"
                            value={newProject.billingMethod}
                            onChange={e => setNewProject({ ...newProject, billingMethod: e.target.value })}
                        >
                            <option value="Hourly Credit">Hourly Credit</option>
                            <option value="Fixed Cost">Fixed Cost</option>
                            <option value="Non-Billable">Non-Billable</option>
                        </select>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {newProject.billingMethod === 'Fixed Cost' ? "Fixed Price" : "Hourly Rate"}
                            </label>
                            <input
                                type="number"
                                placeholder={newProject.billingMethod === 'Fixed Cost' ? "Fixed Price" : "Hourly Rate"}
                                className="w-full border p-2 rounded"
                                value={newProject.hourlyRate || ''}
                                onChange={e => setNewProject({ ...newProject, hourlyRate: parseFloat(e.target.value) })}
                            />
                        </div>
                        {newProject.billingMethod === 'Fixed Cost' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Received Amount
                                    </label>
                                    <input
                                        type="number"
                                        placeholder="Received Amount"
                                        className="w-full border p-2 rounded"
                                        value={newProject.receivedAmount || ''}
                                        onChange={e => setNewProject({ ...newProject, receivedAmount: parseFloat(e.target.value) })}
                                    />
                                </div>
                                <div className="flex justify-between text-sm font-medium text-gray-700 px-1">
                                    <span>Remaining Amount:</span>
                                    <span>{((newProject.hourlyRate || 0) - (newProject.receivedAmount || 0)).toFixed(2)}</span>
                                </div>
                            </>
                        )}
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setShowProjectCreator(false)} className="px-4 py-2 border rounded">Cancel</button>
                            <button onClick={createProject} className="px-4 py-2 bg-blue-600 text-white rounded">Create</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (showCustomerCreator) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                    <h3 className="text-lg font-bold mb-4">Create New Customer</h3>
                    <div className="space-y-4">
                        <input
                            placeholder="Display Name*"
                            className="w-full border p-2 rounded"
                            value={newCustomer.displayName}
                            onChange={e => setNewCustomer({ ...newCustomer, displayName: e.target.value })}
                        />
                        <select
                            className="w-full border p-2 rounded"
                            value={newCustomer.customerType}
                            onChange={e => setNewCustomer({ ...newCustomer, customerType: e.target.value })}
                        >
                            <option value="Business">Business</option>
                            <option value="Individual">Individual</option>
                        </select>
                        <input
                            placeholder="Company Name"
                            className="w-full border p-2 rounded"
                            value={newCustomer.companyName}
                            onChange={e => setNewCustomer({ ...newCustomer, companyName: e.target.value })}
                        />
                        <input
                            placeholder="Email"
                            className="w-full border p-2 rounded"
                            value={newCustomer.email}
                            onChange={e => setNewCustomer({ ...newCustomer, email: e.target.value })}
                        />
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setShowCustomerCreator(false)} className="px-4 py-2 border rounded">Cancel</button>
                            <button onClick={createCustomer} className="px-4 py-2 bg-blue-600 text-white rounded">Create</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (showCustomerCreator) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                    <h3 className="text-lg font-bold mb-4">Create New Customer</h3>
                    <div className="space-y-4">
                        <input
                            placeholder="Display Name*"
                            className="w-full border p-2 rounded"
                            value={newCustomer.displayName}
                            onChange={e => setNewCustomer({ ...newCustomer, displayName: e.target.value })}
                        />
                        <select
                            className="w-full border p-2 rounded"
                            value={newCustomer.customerType}
                            onChange={e => setNewCustomer({ ...newCustomer, customerType: e.target.value })}
                        >
                            <option value="Business">Business</option>
                            <option value="Individual">Individual</option>
                        </select>
                        <input
                            placeholder="Company Name"
                            className="w-full border p-2 rounded"
                            value={newCustomer.companyName}
                            onChange={e => setNewCustomer({ ...newCustomer, companyName: e.target.value })}
                        />
                        <input
                            placeholder="Email"
                            className="w-full border p-2 rounded"
                            value={newCustomer.email}
                            onChange={e => setNewCustomer({ ...newCustomer, email: e.target.value })}
                        />
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setShowCustomerCreator(false)} className="px-4 py-2 border rounded">Cancel</button>
                            <button onClick={createCustomer} className="px-4 py-2 bg-blue-600 text-white rounded">Create</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (showTaskCreator) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                    <h3 className="text-lg font-bold mb-4">Create New Task</h3>
                    <div className="space-y-4">
                        <input
                            placeholder="Task Name*"
                            className="w-full border p-2 rounded"
                            value={newTask.taskName}
                            onChange={e => setNewTask({ ...newTask, taskName: e.target.value })}
                        />
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setShowTaskCreator(false)} className="px-4 py-2 border rounded">Cancel</button>
                            <button onClick={createTask} className="px-4 py-2 bg-blue-600 text-white rounded">Create</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-2xl flex flex-col">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {entry ? 'Edit Log Entry' : 'New Log Entry'}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6">
                    <form id="timesheet-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">

                        <div className="grid grid-cols-2 gap-6">
                            {/* Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Date*</label>
                                <input
                                    type="date"
                                    {...register('date', { required: 'Date is required' })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                                {errors.date && <p className="text-red-500 text-sm">{errors.date.message as string}</p>}
                            </div>

                            {/* Time Spent */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Time Spent (HH:MM)*</label>
                                <div className="relative">
                                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                    <input
                                        type="text"
                                        placeholder="01:00"
                                        {...register('timeSpent', {
                                            required: 'Time is required',
                                            pattern: {
                                                value: /^([0-9]{1,2}):([0-5][0-9])$/,
                                                message: "Format must be HH:MM"
                                            }
                                        })}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                {errors.timeSpent && <p className="text-red-500 text-sm">{errors.timeSpent.message as string}</p>}
                            </div>
                        </div>

                        {/* Project */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Project Name*</label>
                            <div className="flex gap-2">
                                <select
                                    {...register('projectId', { required: 'Project is required' })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="">Select Project</option>
                                    {projects.map((p: any) => (
                                        <option key={p.id} value={p.id}>{p.projectName} - {p.customer?.displayName}</option>
                                    ))}
                                </select>
                                <button type="button" onClick={() => setShowProjectCreator(true)} className="p-2 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 text-gray-600" title="Create New Project">
                                    <Plus size={20} />
                                </button>
                            </div>
                            {errors.projectId && <p className="text-red-500 text-sm">{errors.projectId.message as string}</p>}
                        </div>

                        {/* Task */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Task Name</label>
                            <div className="flex gap-2">
                                <select
                                    {...register('taskId')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    disabled={!selectedProject || tasks.length === 0}
                                >
                                    <option value="">Select Task</option>
                                    {tasks.map((t: any) => (
                                        <option key={t.id} value={t.id}>{t.taskName}</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    onClick={() => setShowTaskCreator(true)}
                                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 text-gray-600 disabled:opacity-50"
                                    disabled={!selectedProject}
                                    title="Create New Task"
                                >
                                    <Plus size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Billable field removed from UI, defaults to true */}

                        {/* Total Amount */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Total Amount</label>
                            <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    readOnly
                                    value={totalAmount}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                                />
                            </div>
                        </div>

                        {/* Notes */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                            <textarea
                                {...register('notes')}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>

                    </form>
                </div>

                <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg flex gap-3">
                    <button
                        type="submit"
                        form="timesheet-form"
                        className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition"
                    >
                        <Save size={18} />
                        Save
                    </button>
                    <button
                        onClick={onClose}
                        className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition text-gray-700"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TimesheetForm;
